import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context';
import api from '../api/client';

const TYPES = ['feature', 'bug', 'design', 'update', 'suggestion'];

const TYPE_META = {
  feature:    { icon: '✨', color: '#7b56b8', label: 'Funkcija' },
  bug:        { icon: '🐛', color: '#cc4444', label: 'Klaida' },
  design:     { icon: '🎨', color: '#4488cc', label: 'Dizainas' },
  update:     { icon: '🔄', color: '#44aa66', label: 'Atnaujinimas' },
  suggestion: { icon: '💡', color: '#f0c040', label: 'Pasiūlymas' },
};

const STATUS_META = {
  pending:      { label: 'Laukiama',     color: '#888',    bg: 'rgba(136,136,136,0.12)' },
  approved:     { label: 'Patvirtinta',  color: '#44aa66', bg: 'rgba(68,170,102,0.12)' },
  under_review: { label: 'Peržiūrima',  color: '#4488cc', bg: 'rgba(68,136,204,0.12)' },
  rejected:     { label: 'Atmesta',      color: '#cc4444', bg: 'rgba(204,68,68,0.12)' },
  done:         { label: 'Įgyvendinta', color: '#f0c040', bg: 'rgba(240,192,64,0.12)' },
};

function TypeBadge({ type }) {
  const m = TYPE_META[type] || { icon: '❓', color: '#888', label: type };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 5, background: m.color + '22', border: `1px solid ${m.color}55`, color: m.color }}>
      {m.icon} {m.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, color: '#888', bg: 'rgba(0,0,0,0.1)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 5, background: m.bg, border: `1px solid ${m.color}44`, color: m.color }}>
      {m.label}
    </span>
  );
}

function WishCard({ wish, user, isAdmin, onVote, onStatusChange, onDelete }) {
  const [voting, setVoting] = useState(false);
  const [changing, setChanging] = useState(false);

  const handleVote = async () => {
    if (!user || voting) return;
    setVoting(true);
    try { await onVote(wish); } finally { setVoting(false); }
  };

  const handleStatus = async (status) => {
    if (!isAdmin || changing) return;
    setChanging(true);
    try { await onStatusChange(wish.id, status); } finally { setChanging(false); }
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', transition: 'border-color .2s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Vote button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, minWidth: 48 }}>
          <button
            onClick={handleVote}
            disabled={!user || voting}
            style={{
              background: wish.my_vote ? 'rgba(240,192,64,0.2)' : 'rgba(255,255,255,0.05)',
              border: `2px solid ${wish.my_vote ? 'rgba(240,192,64,0.6)' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: 8, color: wish.my_vote ? '#f0c040' : 'rgba(200,180,255,0.6)',
              cursor: user ? 'pointer' : 'default', padding: '8px 10px',
              fontSize: 18, lineHeight: 1, transition: 'all .18s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}
          >
            ▲
            <span style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700 }}>{wish.vote_count ?? 0}</span>
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <TypeBadge type={wish.type} />
            <StatusBadge status={wish.status} />
          </div>
          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
            {wish.title}
          </div>
          {wish.description && (
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, margin: '0 0 8px' }}>{wish.description}</p>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>👤 {wish.author_name}</span>
            <span>🕒 {(wish.created_at || '').slice(0, 10)}</span>
          </div>
        </div>

        {/* Admin / owner actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          {isAdmin && (
            <select
              value={wish.status}
              onChange={e => handleStatus(e.target.value)}
              disabled={changing}
              style={{ background: 'var(--bg)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
            >
              <option value="pending">Laukiama</option>
              <option value="approved">Patvirtinta</option>
              <option value="under_review">Peržiūrima</option>
              <option value="rejected">Atmesta</option>
              <option value="done">Įgyvendinta</option>
            </select>
          )}
          {(isAdmin || (user && user.id === wish.user_id)) && (
            <button
              onClick={() => onDelete(wish.id)}
              style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.3)', color: '#ff8080', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
            >🗑️</button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewWishForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ type: 'feature', title: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { setError('Pavadinimas privalomas'); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post('/wishlist', form);
      onCreated(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Klaida');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid rgba(240,192,64,0.2)', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: 'var(--gold2)', marginBottom: 16 }}>✨ Naujas pageidavimas</div>
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 14, marginBottom: 14 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Tipas</label>
          <select className="form-input" value={form.type} onChange={e => set('type', e.target.value)}>
            {TYPES.map(t => (
              <option key={t} value={t}>{TYPE_META[t]?.icon} {TYPE_META[t]?.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Pavadinimas *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Trumpai apibūdinkite pageidavimą..." maxLength={200} />
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">Aprašymas (neprivaloma)</label>
        <textarea className="form-textarea" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Plačiau paaiškinkite pageidavimą..." maxLength={2000} />
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '⏳ Siunčiama...' : '📤 Pateikti'}</button>
        <button className="btn btn-secondary" onClick={onCancel}>Atšaukti</button>
      </div>
    </div>
  );
}

const PAGE_SIZE = 10;

export default function WishlistPage() {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [myVotes, setMyVotes] = useState(new Set());
  const [page, setPage] = useState(1);

  const setTypeFilterAndReset = (v) => { setTypeFilter(v); setPage(1); };
  const setStatusFilterAndReset = (v) => { setStatusFilter(v); setPage(1); };

  const load = useCallback(() => {
    setLoading(true);
    const params = [];
    if (statusFilter) params.push(`status=${statusFilter}`);
    if (typeFilter) params.push(`type=${typeFilter}`);
    const qs = params.length ? '?' + params.join('&') : '';
    api.get(`/wishlist${qs}`)
      .then(r => {
        const data = r.data || [];
        if (user) {
          api.get('/wishlist').then(() => {}).catch(() => {});
          const ids = new Set(data.filter(w => w.my_vote).map(w => w.id));
          setMyVotes(ids);
        }
        setWishes(data.map(w => ({ ...w, my_vote: myVotes.has(w.id) })));
      })
      .catch(() => setWishes([]))
      .finally(() => setLoading(false));
  }, [typeFilter, statusFilter, user]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const loadVotes = useCallback(async (wishList) => {
    if (!user) return wishList;
    const votes = await Promise.all(
      wishList.map(w => api.get(`/wishlist/${w.id}/my-vote`).then(r => r.data.voted).catch(() => false))
    );
    return wishList.map((w, i) => ({ ...w, my_vote: votes[i] }));
  }, [user]);

  useEffect(() => {
    if (!user || !wishes.length) return;
    loadVotes(wishes).then(setWishes);
  }, [user]); // eslint-disable-line

  const handleVote = async (wish) => {
    if (!user) return;
    try {
      if (wish.my_vote) {
        const r = await api.delete(`/wishlist/${wish.id}/vote`);
        setWishes(prev => prev.map(w => w.id === wish.id ? { ...w, my_vote: false, vote_count: r.data.vote_count } : w));
      } else {
        const r = await api.post(`/wishlist/${wish.id}/vote`);
        setWishes(prev => prev.map(w => w.id === wish.id ? { ...w, my_vote: true, vote_count: r.data.vote_count } : w));
      }
    } catch { /* ignore */ }
  };

  const handleStatusChange = async (id, status) => {
    await api.patch(`/wishlist/${id}/status`, { status });
    setWishes(prev => prev.map(w => w.id === id ? { ...w, status } : w));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ar tikrai norite ištrinti?')) return;
    await api.delete(`/wishlist/${id}`);
    setWishes(prev => prev.filter(w => w.id !== id));
  };

  const handleCreated = (wish) => {
    setWishes(prev => [{ ...wish, my_vote: false }, ...prev]);
    setShowForm(false);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(wishes.length / PAGE_SIZE));
  const pagedWishes = wishes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>← {t('common.back')}</button>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 32 }}>💡</span>
              <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 28, color: '#f0c040', margin: 0, fontWeight: 700 }}>Pageidavimai</h1>
            </div>
            <p style={{ color: 'rgba(200,180,255,0.6)', fontSize: 14, margin: 0 }}>
              Siūlykite funkcijas, praneškite apie klaidas, balsuokite už kitų idėjas
            </p>
          </div>
          {user && !showForm && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>✨ Pateikti pageidavimą</button>
          )}
        </div>
      </div>

      {/* New wish form */}
      {showForm && <NewWishForm onCreated={handleCreated} onCancel={() => setShowForm(false)} />}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <select
          value={typeFilter}
          onChange={e => setTypeFilterAndReset(e.target.value)}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
        >
          <option value="">Visi tipai</option>
          {TYPES.map(t => <option key={t} value={t}>{TYPE_META[t]?.icon} {TYPE_META[t]?.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilterAndReset(e.target.value)}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
        >
          <option value="">Visi statusai</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {isAdmin && (
          <select
            value={statusFilter}
            onChange={e => setStatusFilterAndReset(e.target.value)}
            style={{ background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.3)', color: '#ffa040', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
          >
            <option value="">🛡️ Visi (Admin)</option>
            <option value="pending">⏳ Laukiama peržiūros</option>
            <option value="approved">✅ Patvirtinta</option>
            <option value="under_review">🔍 Peržiūrima</option>
            <option value="rejected">❌ Atmesta</option>
            <option value="done">🎉 Įgyvendinta</option>
          </select>
        )}
      </div>

      {/* Stats */}
      {!loading && (
        <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(TYPE_META).map(([type, m]) => {
            const count = wishes.filter(w => w.type === type).length;
            if (!count) return null;
            return (
              <button key={type} onClick={() => setTypeFilterAndReset(typeFilter === type ? '' : type)}
                style={{ background: typeFilter === type ? m.color + '22' : 'var(--bg2)', border: `1px solid ${m.color}${typeFilter === type ? '88' : '44'}`, borderRadius: 8, padding: '6px 14px', color: m.color, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                {m.icon} {m.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Grid / List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>⏳ Kraunama...</div>
      ) : wishes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>💡</div>
          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--text3)' }}>Pageidavimų dar nėra</div>
          {user && !showForm && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>Pateikti pirmą pageidavimą</button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            {pagedWishes.map(wish => (
              <WishCard
                key={wish.id}
                wish={wish}
                user={user}
                isAdmin={isAdmin}
                onVote={handleVote}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 32 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: page === 1 ? 'var(--text3)' : 'var(--text)', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
              >← Atgal</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  style={{ background: n === page ? 'rgba(240,192,64,0.18)' : 'var(--bg2)', border: `1px solid ${n === page ? 'rgba(240,192,64,0.5)' : 'var(--border2)'}`, color: n === page ? '#f0c040' : 'var(--text)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: n === page ? 700 : 400, cursor: 'pointer', minWidth: 36 }}
                >{n}</button>
              ))}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: page === totalPages ? 'var(--text3)' : 'var(--text)', borderRadius: 8, padding: '7px 16px', fontSize: 13, cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
              >Pirmyn →</button>
            </div>
          )}
        </>
      )}

      {!user && (
        <div style={{ marginTop: 32, background: 'rgba(90,62,140,0.1)', border: '1px dashed rgba(90,62,140,0.4)', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>Prisijunkite norėdami pateikti pageidavimą ir balsuoti</div>
        </div>
      )}
    </div>
  );
}
